<!--

-->
<template>
  <div>
    <div class="replacement_item_form">
      <div v-if="!loading">
        <h2 class="subtitle">{{ $t(`${pagei18nKey}.title`) }}</h2>
        <div v-if="claimStatus.replacementItemsNeedsConfirmation">
          <p v-if="claimStatus.replacementStocks.length === 1">{{ $t(`${pagei18nKey}.singleItemReplacement`) }}</p>
          <p v-if="claimStatus.replacementStocks.length > 1">{{ $t(`${pagei18nKey}.multipleItemReplacement`) }}</p>
          <app-image-box
            v-for="(replacement, index) in claimStatus.replacementStocks"
            :key="index"
            class="replacement_item__box"
            :caption="getCaptionForItem(replacement)"
            :image="`${replacement.photo}`"
          />
        </div>
      </div>

      <div v-else>
        <div class="loading-div" v-if="loading">
          <img class="spinner" :src="loadingSpinner"/>
        </div>
      </div>

    </div>
    <!-- Navigation buttons -->
    <div class="replacement_item_button__container">
      <ms-navigation-bar
        :buttons="[backButton, nextButton]"
        @navigationButtonClick="navigationButtonClick"
      />
    </div>

    <ms-app-message
      :title="$t(`${pagei18nKey}.declineModalTitle`)"
      :isOpen.sync="showDeclineReasonModal">
      <p class="modal-warning-text">{{ $t(`${pagei18nKey}.declineModalWarning`) }}</p>
      <p>{{ $t(`${pagei18nKey}.declineModalText`) }}</p>
      <textarea class="textarea has-fixed-size" 
        v-model="replacementStatusReason"></textarea>
      <footer>
        <app-button
          class="modal-card-button"
          :text="$t(yesButton.text)"
          :type="yesButton.type"
          :state="yesButton.state"
          @click.native="makeReplacementStatusUpdate()" />
      </footer>
    </ms-app-message>

  </div>
</template>
<script>
  import api from '@/api/api'
  import AppSettings from '@/app-settings.json'
  import {ButtonStates, ButtonTypes} from '@/components/app-button'
  import AppImageBox from '@/components/app-image-box'
  import MsNavigationBar, {DefaultNavigationButtons} from '@/components/ms-navigation-bar'
  import MsAppMessage, {defaultModalButtons} from '@/components/ms-app-message'
  import {ReplacementStatusUpdate} from '../../model/api/replacementStatusUpdate'

  export default {
    name: 'ReplacementItemView',
    components: {
      MsNavigationBar,
      AppImageBox,
      MsAppMessage
    },
    data: function () {
      return {
        loadingSpinner: AppSettings.submitInProgress,
        loading: false,
        pagei18nKey: 'message.claimStatus.replacementItemsConfirmation',
        replacementItemsConfirmed: false,
        showDeclineReasonModal: false,
        replacementStatusReason: '',
        verificationCode: null
      }
    },
    props: {
      guid: String
    },
    /**
     * Check whether we have route props and if they match a given object in
     * item array then set relevant props to pass through to form components
     */
    beforeMount () {
      this.loading = true
      // see if we have a verifcation code yet?
      this.verificationCode = this.$store.getters.verificationCode
      if (!this.verificationCode) {
        this.$router.push(`/claim-status/verify/${this.guid}`)
        return
      }
  
      api.getClaimStatus(this.guid, this.verificationCode)
        .then(response => {
          this.loading = false
          this.claimStatus = response.data

          if (!this.claimStatus.replacementItemsNeedsConfirmation) {
            this.$router.push(`/claim-status/${this.guid}`)
          }
        })
        .catch(error => {
          console.error(error)
          // check for no auth error
          if (error.response) {
            // TODO update code when server does
            if (error.response.status >= 401 && error.response.status <= 499) {
              // console.log(error.response.status)
              this.$router.push(`/claim-status/verify/${this.guid}`)
            }
          }
        })
    },
    computed: {
      yesButton: {
        get () {
          return Object.assign(
            defaultModalButtons.YES,
            {
              'text': 'message.claimStatus.replacementItemsConfirmation.confirmButtonText'
            }
          )
        }
      },

      /**
       * Get back button object
       */
      backButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: ButtonTypes.TERTIARY,
            text: 'message.claimStatus.replacementItemsConfirmation.declineButtonText',
            hasMaxWidth: true,
            id: 'back'
          }
        }
      },

      /**
       * Get next button object
       */
      nextButton: {
        get () {
          return {
            type: ButtonStates.PRIMARY,
            text: 'message.claimStatus.replacementItemsConfirmation.confirmButtonText',
            hasMaxWidth: true,
            id: 'next'
          }
        }
      }
    },
    methods: {
      /**
       * Gets caption for the given stock item
       */
      getCaptionForItem (item) {
        let captionItems = []
        if (item.name) captionItems.push(this.tryTranslate('message.apiTranslations', item.name))
        if (item.materialName) captionItems.push(this.tryTranslate('message.apiTranslations', item.materialName))
        if (item.size) captionItems.push(item.size)

        let caption = ''
        for (let i = 0; i < captionItems.length; i++) {
          if (i + 1 < captionItems.length) {
            caption += captionItems[i] + ', '
          } else {
            caption += captionItems[i]
          }
        }

        if (item.mainDescription) {
          if (caption.length > 0) {
            caption += `. ${this.tryTranslate('message.apiTranslations', item.mainDescription)}`
          } else {
            caption += `${this.tryTranslate('message.apiTranslations', item.mainDescription)}`
          }
        }

        return caption
      },

      /**
       * i18n doesn't have fallback to plain string option, this function does that instead.
       * Takes path to the translations and tries to translate
       * provided API string. If it doesn't find it, fallbacks to the provided one
       * @param {string} translationPath - e.g 'message.apiTranslations'
       * @param {string} stringToTranslate
       */
      tryTranslate (translationPath, stringToTranslate) {
        let translation = this.$t(`${translationPath}["${stringToTranslate}"]`)

        // In case of unsuccessful translation, translation contains message path
        return translation.includes(translationPath) ? stringToTranslate : translation
      },

      /**
       * Change route
       *
       * @param {string} buttonId - Id of buttonk
       */
      navigationButtonClick (buttonId) {
        if (buttonId === DefaultNavigationButtons.BACK.id) {
          this.openModal()
        } else if (buttonId === DefaultNavigationButtons.NEXT.id) {
          this.replacementItemsConfirmed = true
          this.makeReplacementStatusUpdate()
        }
      },

      makeReplacementStatusUpdate () {
        var replacementStatusUpdate = new ReplacementStatusUpdate({
          replacementItemsConfirmed: this.replacementItemsConfirmed,
          replacementStatusReason: this.replacementStatusReason
        })

        this.closeModal()
        this.loading = true
        api.updateClaimReplacementStatus(this.guid, this.verificationCode, replacementStatusUpdate)
          .then(result => {
            this.$router.push(`/claim-status/${this.guid}`)
          })
          .catch(error => {
            this.loading = false
            console.error(error)
            // check for no auth error
            if (error.response) {
              // TODO update code when server does
              if (error.response.status >= 401 && error.response.status <= 499) {
                // console.log(error.response.status)
                this.$router.push(`/claim-status/verify/${this.guid}`)
              }
            }
          })
      },

      closeModal () {
        this.showDeclineReasonModal = false
      },

      openModal () {
        this.showDeclineReasonModal = true
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../assets/sass/main.scss";

  .replacement_item_button__container {
    @include mobile {
      padding: 0 $dims-horizontal-gutter;
    }
  }

  .replacement_item_form__label {
    font-weight: $semibold-font-weight;
  }

  .replacement_item_form {
    @extend %app_form_container;
    p {
      padding-bottom: $dims-padding-default;
    }
  }

  .replacement_item__checkbox, .replacement_item__box {
    padding-top: $dims-padding-default;
  }

  .replacement_item__box:first-of-type {
    padding-top: 0;
  }

  .loading-div {
    position: relative;
    height: 100px;

    .spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .modal-warning-text {
    margin-bottom: $dims-padding-default;
  }

  .textarea {
    margin-top: $dims-padding-default;
    margin-bottom: $dims-padding-default;
  }

  .modal-card-button {
    width: 100%;
    padding: $dims-spacing-small 0;
  }

</style>
